import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Modal from "../../framework/modal/Modal";
import classes from "./SelectModal.module.css";
import Checkbox from "../../framework/Checkbox";
import ChipInputSelect from "../../framework/ChipInputSelect";
import { columnDefs } from "./ProductsConstants";
import GlobalContext from "../../framework/GlobalContext";
import SelectInput from "../../framework/SelectInput";
import { fetchBranches } from "../../services/branch-service";
import { displayToast } from "../../framework/toast/toastUtils";
import { listBranchGroup } from "../../services/branch-group-services";
import UploadButton from "../../framework/UploadButton";
import { useSelector } from "react-redux";
import DownloadData from "./DownloadData";
import CommonModal from "../../common/modal/CommonModal";
import { bulkUpload } from "../../services/medicine-services";

const SelectModal = (props) => {
  const { showModal = false, onEscape = () => {}, isDownload = false } = props;
  const { data } = useSelector((state) => {
    return state.commonReducer.tabs[state.commonReducer?.activeTabIndex];
  });
  const { setCurrentComponent, setShowLoading } = useContext(GlobalContext);
  const headersRef = useRef(null);
  const branchRef = useRef(null);
  const branchesRef = useRef(null);
  const [selectedHeaders, setSelectedHeaders] = useState([]);
  const [branchSuggestions, setBranchSuggestions] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [branchCodes, setBranchCodes] = useState([]);
  const [errorStates, setErrorStates] = useState({});
  const [failedCount, setFailedCount] = useState(0);
  const [validCount, setValidCount] = useState(0);
  const [allInvalidProduct, setAllInvalidProduct] = useState([]);
  const [validProducts, setValidProducts] = useState([]);
  const [allVerified, setAllVerified] = useState(false);
  const [uploadPayload, setUploadPayload] = useState(null);

  const fetchBranchesWithBranchGroup = useCallback(async () => {
    try {
      const _branch = await fetchBranches();
      const _branchGroup = await listBranchGroup();
      const mergedBranches = [
        ...(isDownload ? [] : _branchGroup?.result?.rows || []),
        ...(_branch.result?.rows || []),
      ];

      setBranchSuggestions(mergedBranches || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, [isDownload]);

  const handleFetchBranches = useCallback((event) => {
    setBranchSuggestions([]);
    fetchBranches(`deleted=false`).then((resp) => {
      let updatedResp = resp.result.rows.map((branch) => ({
        ...branch,
        id: branch._id,
        label: branch.code,
      }));
      setBranchSuggestions(updatedResp);
    });
  }, []);

  const handleBranchSelections = useCallback((options) => {
    setBranchData(options);
    const uniqueBranches = [
      ...new Set(
        options.flatMap((item) =>
          item.branchCode ? item.branchCode : item.code ? [item.code] : []
        )
      ),
    ];
    setBranchCodes(uniqueBranches);
  }, []);

  const defaultSelectedHeaders = useMemo(() => {
    return ["medicineId", "medicineNameDetailed"];
  }, []);

  const headerOptions = useMemo(
    () =>
      columnDefs.filter((col) => {
        return (
          col.field !== "medicineId" &&
          col.field !== "medicineNameDetailed" &&
          col.field !== "category"
        );
      }),
    []
  );

  const handleHeaderSelection = useCallback(
    (options) => {
      const fields = options.map((option) => option.field);
      if (fields.length > 0) {
        setSelectedHeaders([...defaultSelectedHeaders, ...fields]);
      }
    },
    [defaultSelectedHeaders]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedHeaders([...defaultSelectedHeaders]);
    setBranchData(null);
    setErrorStates({});
    setBranchCodes([]);
    onEscape();
  }, [defaultSelectedHeaders, onEscape]);

  const handleValidation = useCallback(
    (id) => {
      if ((!branchData || branchData?.length === 0) && showModal) {
        displayToast({ type: "error", title: "Please select branch" });
        setErrorStates((prev) => {
          return { ...prev, [id]: true };
        });
      }
    },
    [branchData, showModal, setErrorStates]
  );

  const handleBulkUpload = useCallback(
    async (payload, forceUpload) => {
      setShowLoading(true);
      payload.force_upload = forceUpload;
      await bulkUpload(payload)
        .then((response) => {
          const summary = response?.result?.summary || {};
          const invalidCount = summary.product_not_exists_count || 0;
          setValidCount(summary.product_exists_count);
          setAllInvalidProduct(response?.result?.rows?.product_not_exists);
          if (
            response?.status === "success" &&
            validCount === 0 &&
            invalidCount > 0
          ) {
            setFailedCount(invalidCount);
            setAllInvalidProduct(response?.result?.rows?.product_not_exists);
            setValidProducts(response?.result?.rows?.product_exists);
          } else if (
            response?.status === "success" &&
            response?.result?.summary?.product_save_count &&
            response?.result?.summary?.product_save_count !== 0
          ) {
            displayToast({
              type: "success",
              title:
                response?.result?.summary?.product_save_count +
                " product Updated Successfully",
            });
            setFailedCount(0);
            setAllVerified(false);
          } else if (invalidCount === 0 && summary.product_exists_count > 0) {
            setAllVerified(true);
            setUploadPayload((prev) => ({
              ...prev,
              products: response?.result?.rows?.product_exists,
            }));
            setValidProducts([]);
          } else {
            setFailedCount(invalidCount);
            setUploadPayload((prev) => ({
              ...prev,
              products: response?.result?.rows?.product_exists,
            }));
          }
        })
        .catch((error) => {
          displayToast({ type: "error", title: "Product upload failed" });
        })
        .finally(() => {
          setShowLoading(false);
          handleCloseModal();
        });
    },
    [handleCloseModal, setShowLoading, validCount]
  );

  const handleParseCSV = useCallback((file, callback) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n").filter(Boolean);
      const headers = lines[0].split(",");
      const result = lines.slice(1).map((line) => {
        const values = line.split(",");
        return headers.reduce((acc, header, index) => {
          acc[header.trim()] = values[index]?.trim();
          return acc;
        }, {});
      });
      callback(result);
    };
    if (file) {
      reader.readAsText(file);
    }
  }, []);

  const handleUpload = useCallback(
    (file) => {
      if (!file || branchCodes?.length === 0) {
        return;
      }
      handleParseCSV(file, async (parsedData) => {
        const payload = {
          branches: branchCodes,
          products: parsedData,
        };
        setUploadPayload(payload);
        await handleBulkUpload(payload, false);
      });
    },
    [branchCodes, handleParseCSV, handleBulkUpload]
  );

  const okHandler = useCallback(() => {
    setAllVerified(false);
    setAllInvalidProduct([]);
    setValidProducts([]);
    if (uploadPayload) {
      handleBulkUpload(uploadPayload, true);
    }
  }, [uploadPayload, handleBulkUpload]);

  const cancelHandler = useCallback(() => {
    displayToast({ type: "error", title: "Product upload failed" });
    setFailedCount(0);
    setAllInvalidProduct([]);
    setValidProducts([]);
  }, []);

  const cancelAllVerifiedModel = useCallback(() => {
    displayToast({ type: "error", title: "Product upload failed" });
    setAllVerified(false);
  }, []);

  const downloadOkHandler = useCallback(() => {
    const csvHeaders = Object.keys(allInvalidProduct[0]).join(",");
    const csvRows = allInvalidProduct.map((obj) =>
      Object.values(obj).join(",")
    );
    const csvContent = [csvHeaders, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "invalid_products.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setAllInvalidProduct([]);
    setFailedCount(0);
    setValidProducts([]);
    setValidCount(0);
  }, [allInvalidProduct]);

  useEffect(() => {
    if (showModal) {
      setCurrentComponent("select_modal_component");
    } else if (failedCount) {
      setCurrentComponent("common-modal");
    } else {
      setTimeout(() => {
        setCurrentComponent("grid_reports");
      }, 300);
    }
  }, [failedCount, setCurrentComponent, showModal]);

  useEffect(() => {
    isDownload ? handleFetchBranches() : fetchBranchesWithBranchGroup();
  }, [fetchBranchesWithBranchGroup, handleFetchBranches, isDownload]);

  useEffect(() => {
    if (showModal && isDownload) {
      branchRef.current.focus();
    }
    if (showModal && !isDownload) {
      setTimeout(() => {
        branchesRef.current.focus();
      }, 300);
    }
  }, [isDownload, showModal]);

  useEffect(() => {
    if (showModal) {
      setSelectedHeaders([
        ...defaultSelectedHeaders,
        ...headerOptions.map((col) => col.field),
      ]);
    }
  }, [defaultSelectedHeaders, headerOptions, showModal]);

  return (
    <div>
      <Modal
        header={isDownload ? "Download Products" : "Upload Products"}
        height="420px"
        open={showModal}
        onEscape={handleCloseModal}
        onClose={handleCloseModal}
      >
        <div className={classes["select-modal"]}>
          <div
            className={
              isDownload
                ? classes["select-modal-branch"]
                : classes["select-modal-branches"]
            }
          >
            {isDownload ? (
              <SelectInput
                id="select_modal_branch"
                label="Branch"
                ref={branchRef}
                options={branchSuggestions}
                getOptionLabel={(option) => option.label || option}
                isOptionEqualToValue={(option, value) => {
                  return option.label === value.label || value === "";
                }}
                disableClearable={false}
                onFocus={() => setCurrentComponent("select_modal_branch")}
                onChange={(e, values) => {
                  setBranchData(values);
                  setErrorStates((prev) => {
                    return { ...prev, select_modal_branch: false };
                  });
                }}
                onBlur={() => {
                  handleValidation("select_modal_branch");
                }}
                error={errorStates.select_modal_branch}
                value={branchData}
                required
                markers={JSON.stringify({
                  up: "",
                  down: "select_modal_headers",
                  right: "select_modal_headers",
                  left: "",
                })}
              />
            ) : (
              <ChipInputSelect
                id="select_modal_branches"
                value={branchData || null}
                options={branchSuggestions}
                name="branch"
                ref={branchesRef}
                getOptionLabel={(option) =>
                  `${option?.name || ""}- ${option?.code ? "BR" : "BG"}`
                }
                onFocus={() => {
                  setCurrentComponent("select_modal_branches");
                }}
                onChange={(event, option) => {
                  handleBranchSelections(option);
                  setErrorStates((prev) => {
                    return { ...prev, select_modal_branches: false };
                  });
                }}
                onBlur={() => {
                  handleValidation("select_modal_branches");
                }}
                error={errorStates.select_modal_branches}
                required
                openOnFocus={false}
                label="Branches"
                multiple={true}
                blurOnSelect={false}
                disableCloseOnSelect={true}
                groupBy={(option) =>
                  option?.code ? "Branches" : "Branch Groups"
                }
                renderGroup={(params) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        padding: "5px",
                        gap: "5px",
                      }}
                      key={params.key}
                    >
                      <div
                        style={{
                          height: "30px",
                          width: "100%",
                          background: "#e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          padding: "5px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {params.group}
                      </div>
                      <div>{params.children}</div>
                    </div>
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  return (
                    <div
                      {...props}
                      style={{
                        display: "flex",
                        height: "40px",
                        width: "100%",
                        padding: "1px 5px",
                        gap: "5px",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItem: "center",
                        }}
                      >
                        <Checkbox checked={selected} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItem: "center",
                          gap: "5px",
                        }}
                      >
                        <div
                          style={{
                            height: "15px",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          {option?.name} (
                          {option?.code || option?.branchGroupId})
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {option.address}
                        </div>
                      </div>
                    </div>
                  );
                }}
                markers={JSON.stringify({
                  up: "",
                  down: "",
                  left: "",
                  right: "",
                })}
              />
            )}
          </div>
          <div className={classes["select-modal-headers"]}>
            {isDownload && (
              <ChipInputSelect
                id="select_modal_headers"
                options={headerOptions}
                name="headers"
                ref={headersRef}
                getOptionLabel={(option) => `${option?.headerName || ""}`}
                isOptionEqualToValue={(option, value) => {
                  return option === value;
                }}
                onFocus={() => setCurrentComponent("select_modal_headers")}
                onChange={(e, option) => {
                  handleHeaderSelection(option);
                }}
                openOnFocus={true}
                label="Columns"
                multiple={true}
                blurOnSelect={false}
                disableCloseOnSelect={true}
                renderOption={(props, option, { selected }) => {
                  return (
                    <div
                      {...props}
                      style={{
                        display: "flex",
                        height: "40px",
                        width: "100%",
                        padding: "1px 5px",
                        gap: "5px",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItem: "center",
                        }}
                      >
                        <Checkbox checked={selected} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItem: "center",
                          gap: "5px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "15px",
                            width: "100%",
                          }}
                        >
                          {option?.headerName}
                        </div>
                      </div>
                    </div>
                  );
                }}
                markers={JSON.stringify({
                  up: "select_modal_branch",
                  left: "select_modal_branch",
                  right:
                    branchData === null
                      ? "select_modal_branch"
                      : "select_modal_download",
                  down: "",
                })}
              />
            )}
          </div>
          <div className={classes["select-modal-ctas"]}>
            {isDownload ? (
              <DownloadData
                id="select_modal_download"
                exportButtonLabel="Download CSV"
                exportFileName="Products Data"
                data={(data?.rows || []).map((row) => ({
                  ...row,
                  manufacturerGroupName:
                    row?.manufacturerGroup?.name || "",
                }))}
                selectedColumns={selectedHeaders}
                selectedBranch={branchData?.code}
                disabled={branchData === null}
                onSuccess={handleCloseModal}
                color="violet"
              />
            ) : (
              <div className={classes["select-modal-cta-upload"]}>
                <UploadButton
                  label="Upload Document"
                  color="success"
                  onUpload={(files) => handleUpload(files[0])}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  disabled={branchCodes?.length === 0}
                  badgeContent={2}
                  showUploadedFile={true}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
      {failedCount > 0 && validCount > 0 && (
        <CommonModal
          type="warn"
          title={`${failedCount} product(s) failed to upload. Would you like to proceed with the remaining valid products?`}
          okButtonText="Proceed"
          cancelButtonText="Cancel"
          showDownloadButton={true}
          okDownloadText="Download Failed Products"
          onDownload={downloadOkHandler}
          onOk={okHandler}
          onCancel={cancelHandler}
          onEscape={cancelHandler}
          onClose={cancelHandler}
        />
      )}
      {allInvalidProduct?.length > 0 &&
        (!validProducts || validProducts.length === 0) && (
          <CommonModal
            type="warn"
            title={
              "All products failed to upload. Would you like to download the products?"
            }
            okButtonText="Download"
            cancelButtonText="Cancel"
            okButtonColor="black"
            onOk={downloadOkHandler}
            onCancel={cancelHandler}
            onEscape={cancelHandler}
            onClose={cancelHandler}
          />
        )}
      {allVerified && (
        <CommonModal
          title={
            "All products have been verified. Would you like to save them?"
          }
          okButtonText="Save"
          cancelButtonText="Cancel"
          onOk={okHandler}
          onCancel={cancelAllVerifiedModel}
          onEscape={cancelAllVerifiedModel}
          onClose={cancelAllVerifiedModel}
        />
      )}
    </div>
  );
};

export default SelectModal;
